import Image from 'next/image';
import Link from 'next/link';
import { IEvent } from '@/lib/types';
import MapPinIcon from '@/assets/icon/map-pin.svg';
import ClockIcon from '@/assets/icon/clock.svg';
import GlobeIcon from '@/assets/icon/globe.svg';
import ArrowRightIcon from '@/assets/icon/arrow-right.svg';
import { SupportedLanguages } from '@/types/globals';
import CalendarIcon from '@/assets/icon/calendar.svg';

interface EventCardProps {
  event: IEvent;
  basePath: string;
  lang: string;
  showDivider?: boolean;
  formatDateLocal: (dateString: string) => {
    year: string;
    day: string;
    month: string;
  };
  formatTimeLocal: (timeString: string) => string;
  acfText: {
    seeDetails: string;
  };
  locale: SupportedLanguages;
}

const text = {
  en: {
    eventWebsite: 'Event Website',
  },
  fr: {
    eventWebsite: "Site de l'événement",
  },
};
export function EventCard({
  event,
  basePath,
  showDivider = false,
  formatDateLocal,
  formatTimeLocal,
  acfText,
  locale,
}: EventCardProps) {
  return (
    <div className="overflow-hidden rounded-lg bg-white">
      {showDivider && <div className="mb-6 border-t border-gray-200"></div>}
      <div className="flex flex-col lg:flex-row">
        {/* Event Image */}
        <div className="relative h-80 lg:h-96 lg:w-2/5">
          {event.acf?.event_image?.sizes?.large &&
          event.acf.event_image.sizes.large.trim() !== '' &&
          event.acf.event_image.sizes.large !== 'null' ? (
            <Image
              src={event.acf.event_image.sizes.large}
              alt={event.title.rendered}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 460px"
            />
          ) : (
            <div className="bg-graphite-200 h-full w-full" />
          )}

          {/* Date Overlay */}
          {event.acf?.event_date_start && (
            <div className="absolute top-4 left-4 rounded-lg bg-white p-3 text-center shadow-sm">
              <div className="text-qf-purple font-inter text-sm text-[14px] leading-[130%] font-medium tracking-[-1.4%]">
                {formatDateLocal(event.acf.event_date_start).year}
              </div>
              <div className="text-qf-purple font-lora text-3xl text-[34px] leading-[130%] font-bold tracking-[-1.4%]">
                {formatDateLocal(event.acf.event_date_start).day}
              </div>
              <div className="text-qf-purple font-inter text-base text-[16px] leading-[130%] font-medium tracking-[-1.4%]">
                {formatDateLocal(event.acf.event_date_start).month}
              </div>
            </div>
          )}
        </div>

        {/* Event Details */}
        <div className="p-6 lg:w-1/2 lg:p-8">
          <h3 className="font-inter mb-4 text-2xl leading-normal font-bold font-semibold tracking-[-1.4%] text-gray-800 lg:text-3xl">
            <span dangerouslySetInnerHTML={{ __html: event.title.rendered }} />
          </h3>

          {event.acf?.event_location && (
            <div className="mb-3">
              <span className="mr-2 mb-2 inline-block rounded bg-blue-100 px-2 py-1 text-sm text-blue-800">
                <span
                  dangerouslySetInnerHTML={{
                    __html: event.acf.event_location.name,
                  }}
                />
              </span>
            </div>
          )}

          {event.acf?.event_details && (
            <p className="text-graphite-700 font-inter mb-6 [display:-webkit-box] overflow-hidden leading-relaxed [-webkit-box-orient:vertical] [-webkit-line-clamp:4]">
              <span
                dangerouslySetInnerHTML={{ __html: event.acf.event_details }}
              />
            </p>
          )}

          {/* Event Details List */}
          <div className="mb-6 space-y-3">
            {event.acf?.address && (
              <div className="flex items-center gap-3">
                <MapPinIcon className="h-4 w-4 text-gray-500" />
                <span
                  className="text-graphite-700 font-inter leading-normal font-medium tracking-[-1.4%]"
                  dangerouslySetInnerHTML={{
                    __html: event.acf.address,
                  }}
                />
              </div>
            )}

            {event.acf?.event_date_end && (
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-4 w-4 text-gray-500" />
                <span className="text-graphite-700 font-inter leading-normal font-medium tracking-[-1.4%]">
                  {formatDateLocal(event.acf.event_date_start).month}{' '}
                  {formatDateLocal(event.acf.event_date_start).day} -{' '}
                  {formatDateLocal(event.acf.event_date_end).month}{' '}
                  {formatDateLocal(event.acf.event_date_end).day}
                </span>
              </div>
            )}

            {event.acf?.start_time && event.acf?.end_time && (
              <div className="flex items-center gap-3">
                <ClockIcon className="h-4 w-4 text-gray-500" />
                <span className="text-graphite-700 font-inter leading-normal font-medium tracking-[-1.4%]">
                  {formatTimeLocal(event.acf.start_time)} -{' '}
                  {formatTimeLocal(event.acf.end_time)}
                </span>
              </div>
            )}

            {event.acf?.event_website && (
              <div className="flex items-center gap-3">
                <GlobeIcon className="h-4 w-4 text-gray-500" />
                <Link
                  href={event.acf.event_website}
                  target="_blank"
                  className="text-graphite-700 font-inter leading-normal font-medium tracking-[-1.4%] underline"
                >
                  {text[locale].eventWebsite}
                </Link>
              </div>
            )}
          </div>

          {/* CTA Button */}
          <Link
            href={`/${basePath}/${event.slug}`}
            className="bg-qf-purple inline-flex items-center gap-6 rounded px-8 py-4 font-semibold text-white transition-colors"
          >
            {acfText.seeDetails}
            <ArrowRightIcon className="h-4 w-4 text-white" />
          </Link>
        </div>
      </div>
    </div>
  );
}
